import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRide } from '../contexts/RideContext';
import { handleApiError } from '../lib/api';
import { MapPin, Package, Utensils, Briefcase, Navigation, Search, X, Loader2, ArrowLeft, CreditCard, Info, AlertCircle, MapPin as MapPinIcon } from 'lucide-react';
import { PaymentMethod } from '@ouagaliv/shared';

const rideTypes = [
  { value: 'delivery', label: 'Livraison', icon: Package, desc: 'Colis, documents, petits objets', color: 'bg-blue-100 text-blue-600' },
  { value: 'errand', label: 'Course', icon: Navigation, desc: 'Acheter, payer, récupérer', color: 'bg-purple-100 text-purple-600' },
  { value: 'meal', label: 'Repas', icon: Utensils, desc: 'Restaurant, fast-food, snack', color: 'bg-orange-100 text-orange-600' },
  { value: 'package', label: 'Colis volumineux', icon: Briefcase, desc: 'Objets encombrants, cartons', color: 'bg-gray-100 text-gray-600' },
];

export default function NewRide() {
  const navigate = useNavigate();
  const { createRide, isLoading: creating } = useRide();
  const [step, setStep] = useState<'type' | 'addresses' | 'confirm'>('type');
  const [selectedType, setSelectedType] = useState('delivery');
  const [pickupAddress, setPickupAddress] = useState('');
  const [pickupCoords, setPickupCoords] = useState<{lat: number; lng: number} | null>(null);
  const [dropoffAddress, setDropoffAddress] = useState('');
  const [dropoffCoords, setDropoffCoords] = useState<{lat: number; lng: number} | null>(null);
  const [notes, setNotes] = useState('');
  const [priceEstimate, setPriceEstimate] = useState(0);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const pickupInputRef = useRef<HTMLInputElement>(null);
  const dropoffInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        setPickupCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      });
    }
  }, []);

  const calculatePrice = () => {
    if (pickupCoords && dropoffCoords) {
      const R = 6371;
      const dLat = (dropoffCoords.lat - pickupCoords.lat) * Math.PI / 180;
      const dLng = (dropoffCoords.lng - pickupCoords.lng) * Math.PI / 180;
      const a = Math.sin(dLat/2)**2 + Math.cos(pickupCoords.lat*Math.PI/180)*Math.cos(dropoffCoords.lat*Math.PI/180)*Math.sin(dLng/2)**2;
      const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      let rate = 500;
      if (selectedType === 'meal') rate = 600;
      if (selectedType === 'package') rate = 700;
      setPriceEstimate(Math.max(500, Math.round(dist * rate)));
    }
  };

  const handleNext = () => {
    if (step === 'type') setStep('addresses');
    else if (step === 'addresses') {
      if (!pickupAddress || !dropoffAddress) {
        setError('Veuillez remplir les deux adresses');
        return;
      }
      calculatePrice();
      setStep('confirm');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pickupCoords || !dropoffCoords) {
      setError('Impossible de calculer l\'itinéraire');
      return;
    }
    setSearching(true);
    setError('');
    try {
      const ride = await createRide({
        type: selectedType as any,
        pickupLat: pickupCoords.lat,
        pickupLng: pickupCoords.lng,
        pickupAddress,
        dropoffLat: dropoffCoords.lat,
        dropoffLng: dropoffCoords.lng,
        dropoffAddress,
        notes,
        paymentMethod: PaymentMethod.CASH,
      });
      navigate(`/ride/${ride.id}`);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSearching(false);
    }
  };

  const clearAddress = (field: 'pickup' | 'dropoff') => {
    if (field === 'pickup') { setPickupAddress(''); setPickupCoords(null); }
    else { setDropoffAddress(''); setDropoffCoords(null); }
  };

  const selectedTypeInfo = rideTypes.find(t => t.value === selectedType);

  return (
    <div className="min-h-screen bg-background">
      <div className="container pt-4 pb-28">
        {/* Progress indicator */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2" role="tablist">
          {['type', 'addresses', 'confirm'].map((s, i) => (
            <button
              key={s}
              role="tab"
              aria-selected={step === s}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                step === s ? 'bg-primary text-white shadow-md' : 'bg-white text-text-secondary hover:bg-gray-50'
              } ${i < ['type', 'addresses', 'confirm'].indexOf(step) ? 'bg-primary/20 text-primary' : ''}`}
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                step === s ? 'bg-white text-primary' : 'bg-gray-200 text-gray-400'
              }`}>
                {i + 1}
              </span>
              {s === 'type' && 'Type'}
              {s === 'addresses' && 'Adresses'}
              {s === 'confirm' && 'Confirmation'}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2 animate-slide-up">
            <AlertCircle size={16} className="flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Step 1: Type Selection */}
        {step === 'type' && (
          <div className="card animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-text">Que souhaitez-vous faire livrer ?</h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {rideTypes.map(type => (
                <button
                  key={type.value}
                  onClick={() => { setSelectedType(type.value); setStep('addresses'); }}
                  className={`p-5 rounded-xl border-2 text-left transition-all duration-300 group ${
                    selectedType === type.value ? 'border-primary bg-primary/5 shadow-md' : 'border-border hover:border-primary/50 hover:bg-primary/5'
                  }`}
                >
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 transition-transform duration-300 group-hover:scale-110 ${type.color}`}>
                    <type.icon size={26} />
                  </div>
                  <p className="font-semibold text-text mb-1">{type.label}</p>
                  <p className="text-xs text-text-secondary">{type.desc}</p>
                  <div className="mt-2 text-xs font-medium text-primary/70 group-hover:opacity-100 opacity-0 transition-opacity">
                    Choisir →
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Addresses */}
        {step === 'addresses' && (
          <form onSubmit={handleNext} className="card animate-slide-up space-y-5">
            <div className="flex items-center gap-2 mb-2">
              <ArrowLeft size={18} className="text-text-secondary" />
              <h2 className="text-xl font-semibold text-text">Adresses</h2>
            </div>

            {/* Pickup Address */}
            <div>
              <label className="input-label flex items-center gap-2">
                <MapPin className="text-primary" size={18} />
                Adresse de départ
              </label>
              <div className="relative mt-2">
                <input
                  ref={pickupInputRef}
                  type="text"
                  className="input-field pr-12"
                  value={pickupAddress}
                  onChange={e => setPickupAddress(e.target.value)}
                  placeholder="Ex: Ouaga 2000, près de la mairie"
                  required
                />
                {pickupAddress && (
                  <button type="button" onClick={() => clearAddress('pickup')} className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text transition-colors p-1" aria-label="Effacer">
                    <X size={20} />
                  </button>
                )}
              </div>
              <p className="text-xs text-text-secondary mt-1">Utilisez votre position actuelle ou tapez l'adresse</p>
            </div>

            {/* Dropoff Address */}
            <div>
              <label className="input-label flex items-center gap-2">
                <MapPin className="text-secondary" size={18} />
                Adresse de destination
              </label>
              <div className="relative mt-2">
                <input
                  ref={dropoffInputRef}
                  type="text"
                  className="input-field pr-12"
                  value={dropoffAddress}
                  onChange={e => setDropoffAddress(e.target.value)}
                  placeholder="Ex: Zone du Bois, chez Mamadou"
                  required
                />
                {dropoffAddress && (
                  <button type="button" onClick={() => clearAddress('dropoff')} className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text transition-colors p-1" aria-label="Effacer">
                    <X size={20} />
                  </button>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="input-label">Note pour le livreur (optionnel)</label>
              <textarea
                className="input-field"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Code porte, étage, instructions spéciales, numéro de téléphone..."
                rows={3}
              />
            </div>

            {/* Payment Method */}
            <div className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <CreditCard size={20} className="text-primary" />
                <div>
                  <p className="font-medium text-text">Paiement à la livraison</p>
                  <p className="text-xs text-text-secondary">Paiement en espèces ou Mobile Money au livreur</p>
                </div>
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-full mt-2">
              <Search size={18} /> Continuer
            </button>
          </form>
        )}

        {/* Step 3: Confirmation */}
        {step === 'confirm' && (
          <div className="card animate-slide-up space-y-5">
            <div className="flex items-center gap-2 mb-2">
              <button type="button" onClick={() => setStep('addresses')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Retour">
                <ArrowLeft size={20} />
              </button>
              <h2 className="text-xl font-semibold text-text flex-1">Confirmation</h2>
            </div>

            {/* Ride Type Summary */}
            <div className="flex items-center gap-3 p-4 bg-primary/5 rounded-xl">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                {selectedTypeInfo?.icon && <selectedTypeInfo.icon size={28} />}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-text">{selectedTypeInfo?.label}</p>
                <p className="text-sm text-text-secondary">{selectedTypeInfo?.desc}</p>
              </div>
              <span className="badge badge-primary badge-lg">{selectedTypeInfo?.label}</span>
            </div>

            {/* Addresses */}
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPinIcon size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-text-secondary uppercase tracking-wider">Départ</p>
                  <p className="font-medium text-text truncate">{pickupAddress}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                  <MapPinIcon size={20} className="text-secondary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-text-secondary">Destination</p>
                  <p className="font-medium text-text truncate">{dropoffAddress}</p>
                </div>
              </div>
            </div>

            {/* Notes */}
            {notes && (
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="flex items-start gap-2">
                  <Info size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-amber-800">Note pour le livreur</p>
                    <p className="text-sm text-amber-700">{notes}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Price Summary */}
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-5 border border-primary/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-text-secondary text-sm">Prix estimé</p>
                  <p className="text-3xl font-bold text-text">{priceEstimate.toLocaleString()} FCFA</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-text-secondary">Commission plateforme (15%)</p>
                  <p className="text-lg font-semibold text-primary">~{(priceEstimate * 0.15).toFixed(0)} FCFA</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-primary/10 flex items-center justify-between text-sm">
                <span className="text-text-secondary">Vous payez</span>
                <span className="font-bold text-text">{priceEstimate.toLocaleString()} FCFA</span>
              </div>
            </div>

            {/* Payment Info */}
            <div className="p-4 bg-white rounded-xl border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <CreditCard size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-text">Paiement à la livraison</p>
                  <p className="text-xs text-text-secondary">Espèces ou Mobile Money (Moov Money, Orange Money)</p>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <button type="submit" onClick={handleSubmit} className="btn btn-primary btn-full btn-lg" disabled={searching}>
              {searching ? (
                <>
                  <span className="loading-spinner" />
                  Recherche d'un livreur...
                </>
              ) : (
                'Confirmer et chercher un livreur'
              )}
            </button>

            <button type="button" onClick={() => setStep('addresses')} className="btn btn-ghost btn-full">
              <ArrowLeft size={18} /> Modifier les adresses
            </button>
          </div>
        )}
      </div>
    </div>
  );
}